import React, { useEffect, useMemo, useState } from 'react'
import { API_BASE_URL } from '../constants'
import { ensureSessionHeaders } from '../lib/serverSession'

type LevelRow = {
  level: number
  minShares: number
  rate: number
  minLegs: number
  maxLegShare: number
  minLegOfLevel: number
}

type LegRow = {
  userId: number
  username: string
  teamName: string | null
  eligibleShares: number
  genealogyShares: number
}

type Reading = {
  hasPoolTeam: boolean
  teamName?: string | null
  legs: LegRow[]
  genealogyShares: number
  eligibleShares: number
  producingLegs: number
  level: number
  rate: number
  qualifiedAt: string | null
}

type Payload = {
  window: { open: boolean; start: string; end: string; phaseName: string }
  levels: LevelRow[]
  reading: Reading
}

const PAGE_SIZE = 8
const LEVEL_COLORS = ['#94a3b8', '#38bdf8', '#22d3ee', '#34d399', '#a3e635', '#facc15', '#fb923c', '#f472b6', '#FFD700']

function percent(rate: number) {
  const value = rate * 100
  return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`
}

function formatDate(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

function legColor(name: string) {
  let hash = 0
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  return `hsl(${hash % 360} 70% 55%)`
}

export function ProductionVeinAccord() {
  const [payload, setPayload] = useState<Payload | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [showRules, setShowRules] = useState(true)
  const [page, setPage] = useState(0)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const headers = await ensureSessionHeaders()
        const response = await fetch(`${API_BASE_URL}/api/teams/vein-accord`, { headers })
        const body = await response.json()
        if (!response.ok || !body.success) {
          throw new Error(body.error || 'Could not load the Global Top Affiliate Pool')
        }
        if (!cancelled) setPayload(body)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the Global Top Affiliate Pool')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const legs = useMemo(() => {
    const rows = payload?.reading.legs || []
    return [...rows].sort((a, b) => b.eligibleShares - a.eligibleShares || b.genealogyShares - a.genealogyShares || a.username.localeCompare(b.username))
  }, [payload])

  const pageCount = Math.max(1, Math.ceil(legs.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pageRows = legs.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)
  const reading = payload?.reading
  const nextLevel = payload?.levels.find((level) => level.level > (reading?.level || 0)) || null
  const sharePct = nextLevel && reading ? Math.min(100, (reading.eligibleShares / nextLevel.minShares) * 100) : 100
  const legPct = nextLevel && reading ? Math.min(100, (reading.producingLegs / nextLevel.minLegs) * 100) : 100

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #111 50%, #0d0d0d 100%)' }}>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold" style={{ color: '#FFD700' }}>Global Top Affiliate Pool</h1>
            <p className="text-gray-500 text-xs mt-1">Same Daily Performance Pool teams • Paid shares only • 1 October 2026 until the Production Capital phase ends</p>
          </div>
          <button
            onClick={() => setShowRules((open) => !open)}
            className="text-xs text-gray-300 hover:text-yellow-300 border border-yellow-700/40 hover:border-yellow-400 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap"
          >
            {showRules ? 'Hide instructions' : 'How it works'}
          </button>
        </div>

        {showRules && (
          <div className="rounded-xl border border-yellow-700/30 p-5 space-y-4" style={{ background: 'linear-gradient(135deg, rgba(120,80,0,0.18), rgba(20,40,80,0.18))' }}>
            <h2 className="text-sm font-bold text-yellow-300">How this pool works</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-cyan-700/30 bg-cyan-950/20 p-3">
                <h3 className="text-cyan-300 font-semibold mb-1">1. Your teams stay the same</h3>
                <p className="text-gray-300">This pool uses the teams you already have in the Daily Performance Pool. A leg is one direct person you referred, plus everyone under them. People are not moved into new groups.</p>
              </div>
              <div className="rounded-lg border border-emerald-700/30 bg-emerald-950/20 p-3">
                <h3 className="text-emerald-300 font-semibold mb-1">2. Only paid shares count</h3>
                <p className="text-gray-300">Count shares a customer paid for from 1 October 2026 until the Production Capital phase ends. Bonus shares, gifts, and commission shares do not count.</p>
              </div>
              <div className="rounded-lg border border-orange-700/30 bg-orange-950/20 p-3">
                <h3 className="text-orange-300 font-semibold mb-1">3. One leg cannot do it alone</h3>
                <p className="text-gray-300">You need the share total and the number of separate legs for a level. From 10,000 paid shares you need 3 legs, and each of those three must carry a real part of the target.</p>
              </div>
              <div className="rounded-lg border border-fuchsia-700/30 bg-fuchsia-950/20 p-3">
                <h3 className="text-fuchsia-300 font-semibold mb-1">4. The first valid leader keeps the shares</h3>
                <p className="text-gray-300">You receive the highest level you fully meet. If a leader under you already has a pool team and qualifies first, those paid shares stay with them. They are not counted again for you.</p>
              </div>
            </div>
            <p className="text-xs text-gray-400">This page is a reading of where you stand. It does not pay USDT and it does not add shares.</p>
          </div>
        )}

        {loading && (
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Checking your Daily Performance Pool legs…</p>
          </div>
        )}
        {error && <div className="rounded-xl border border-red-700/40 bg-red-950/30 p-4 text-red-200">{error}</div>}

        {payload && reading && (
          <>
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: payload.window.open ? 'rgba(34,197,94,0.35)' : 'rgba(251,191,36,0.35)', background: payload.window.open ? 'linear-gradient(135deg, rgba(0,80,40,0.2), rgba(20,20,20,0.4))' : 'linear-gradient(135deg, rgba(120,80,0,0.2), rgba(20,20,20,0.4))' }}>
              <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400">{payload.window.phaseName}</div>
                  <h2 className="text-2xl font-bold text-white mt-1">
                    {reading.level > 0 ? `You are on level ${reading.level}` : 'You are not on a level yet'}
                  </h2>
                  {reading.level === 0 && (
                    <button
                      type="button"
                      onClick={() => document.getElementById('affiliate-pool-levels')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-black"
                      style={{ background: 'linear-gradient(90deg, #FFD700, #f59e0b)' }}
                    >
                      See the levels
                    </button>
                  )}
                  <p className="text-sm text-gray-300 mt-1">
                    {payload.window.open ? 'The pool is open.' : 'The pool is closed.'} It runs from 1 October 2026 until {formatDate(payload.window.end)}.
                  </p>
                </div>
                <div className="text-center px-5 py-3 rounded-xl border border-yellow-500/30" style={{ background: 'rgba(255,215,0,0.08)' }}>
                  <div className="text-[10px] uppercase text-gray-400">Your rate</div>
                  <div className="text-3xl font-bold" style={{ color: '#FFD700' }}>{reading.level > 0 ? percent(reading.rate) : '0%'}</div>
                </div>
              </div>
            </div>

            {!reading.hasPoolTeam ? (
              <div className="rounded-xl border border-orange-700/40 bg-orange-950/20 p-5">
                <h3 className="text-orange-300 font-bold">Register a Daily Performance Pool team first</h3>
                <p className="text-gray-300 text-sm mt-2">This pool does not create a new team. Open Gold Diggers Performance Pool, qualify, and register the team you already use. Then come back here.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-xl p-4 border border-yellow-700/30" style={{ background: 'linear-gradient(135deg, rgba(100,70,0,0.2), rgba(20,20,20,0.4))' }}>
                    <div className="text-gray-400 text-xs">Shares that count for you</div>
                    <div className="text-2xl font-bold text-yellow-300">{reading.eligibleShares.toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl p-4 border border-cyan-700/30 bg-cyan-950/20">
                    <div className="text-gray-400 text-xs">All paid shares under you</div>
                    <div className="text-2xl font-bold text-cyan-300">{reading.genealogyShares.toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl p-4 border border-emerald-700/30 bg-emerald-950/20">
                    <div className="text-gray-400 text-xs">Legs with paid shares</div>
                    <div className="text-2xl font-bold text-emerald-300">{reading.producingLegs}</div>
                  </div>
                  <div className="rounded-xl p-4 border border-fuchsia-700/30 bg-fuchsia-950/20">
                    <div className="text-gray-400 text-xs">Your pool team</div>
                    <div className="text-lg font-bold text-fuchsia-200 truncate">{reading.teamName || 'Pool team'}</div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-700/40 p-5" style={{ background: 'rgba(20,20,20,0.6)' }}>
                  <h3 className="text-sm font-bold text-white mb-3">{nextLevel ? `Next step: level ${nextLevel.level} at ${percent(nextLevel.rate)}` : 'You have reached the top level'}</h3>
                  {nextLevel ? (
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>Paid shares that count</span>
                          <span className={reading.eligibleShares >= nextLevel.minShares ? 'text-emerald-300' : 'text-gray-300'}>{reading.eligibleShares.toLocaleString()} / {nextLevel.minShares.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${sharePct}%`, background: 'linear-gradient(90deg, #facc15, #22c55e)' }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>Separate legs with paid shares</span>
                          <span className={reading.producingLegs >= nextLevel.minLegs ? 'text-emerald-300' : 'text-gray-300'}>{reading.producingLegs} / {nextLevel.minLegs}</span>
                        </div>
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${legPct}%`, background: 'linear-gradient(90deg, #22d3ee, #a78bfa)' }} />
                        </div>
                      </div>
                      <p className="text-xs text-gray-400">The strongest leg may be at most {(nextLevel.maxLegShare * 100).toFixed(0)}% of the shares that count. {nextLevel.minLegOfLevel > 0 ? `Each of the ${nextLevel.minLegs} required legs needs at least ${(nextLevel.minLegOfLevel * 100).toFixed(0)}% of ${nextLevel.minShares.toLocaleString()} shares.` : 'A leg counts when it has paid shares, not a zero.'}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-emerald-300">Level 9 is the highest rate in this pool.</p>
                  )}
                </div>

                <div className="rounded-xl border border-gray-700/40 overflow-hidden" style={{ background: 'rgba(15,18,28,0.7)' }}>
                  <div className="px-5 py-3 border-b border-gray-700/40 flex items-center justify-between">
                    <h3 className="text-sm font-bold text-white">Your legs</h3>
                    <span className="text-xs text-gray-400">{legs.length} direct {legs.length === 1 ? 'branch' : 'branches'}</span>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {pageRows.map((leg, index) => {
                      const name = leg.username || `User ${leg.userId}`
                      const stripe = legColor(name)
                      const rank = safePage * PAGE_SIZE + index + 1
                      return (
                        <div key={leg.userId} className="rounded-xl border border-gray-700/40 bg-gray-900/40 p-4" style={{ borderLeft: `4px solid ${stripe}` }}>
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <div className="text-xs text-gray-500">Leg {rank}</div>
                              <div className="font-semibold text-white">{name}</div>
                              {leg.teamName && <div className="text-xs" style={{ color: stripe }}>{leg.teamName}</div>}
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-yellow-300">{leg.eligibleShares.toLocaleString()}</div>
                              <div className="text-[10px] uppercase text-gray-500">counts for you</div>
                            </div>
                          </div>
                          <div className="mt-3 text-xs text-cyan-200">All paid shares in this leg: {leg.genealogyShares.toLocaleString()}</div>
                        </div>
                      )
                    })}
                    {pageRows.length === 0 && <p className="text-gray-400 text-sm">You do not have a direct referral branch yet.</p>}
                  </div>
                  {legs.length > PAGE_SIZE && (
                    <div className="px-4 pb-4 flex items-center justify-between gap-2">
                      <button
                        onClick={() => setPage((current) => Math.max(0, current - 1))}
                        disabled={safePage === 0}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-200 hover:border-yellow-400 disabled:opacity-30"
                      >Previous</button>
                      <span className="text-xs text-gray-400">Page {safePage + 1} of {pageCount}</span>
                      <button
                        onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
                        disabled={safePage >= pageCount - 1}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-200 hover:border-yellow-400 disabled:opacity-30"
                      >Next</button>
                    </div>
                  )}
                </div>
              </>
            )}

            <div id="affiliate-pool-levels" className="scroll-mt-6">
              <h3 className="text-sm font-bold text-white mb-3">The nine levels</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {payload.levels.map((level) => {
                  const color = LEVEL_COLORS[level.level - 1] || '#FFD700'
                  const current = reading.level === level.level
                  return (
                    <div key={level.level} className="rounded-xl p-4 border" style={{ borderColor: current ? color : 'rgba(255,255,255,0.08)', background: current ? `${color}22` : 'rgba(20,20,20,0.55)' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase" style={{ color }}>Level {level.level}</span>
                        {current && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-black" style={{ background: color }}>You are here</span>}
                      </div>
                      <div className="text-2xl font-bold text-white mt-2">{percent(level.rate)}</div>
                      <p className="text-xs text-gray-300 mt-2">{level.minShares.toLocaleString()}+ paid shares</p>
                      <p className="text-xs text-gray-400 mt-1">{level.minLegs} legs • strongest leg at most {(level.maxLegShare * 100).toFixed(0)}%{level.minLegOfLevel > 0 ? ` • each required leg at least ${(level.minLegOfLevel * 100).toFixed(0)}% of the target` : ''}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
